import { useTiming } from '@/hooks/useTiming';
import { useAuth } from '@/services/auth';
import useTtsData from '@/services/tts/tokenManager';
import { getAzureTtsData, getStreamTtsData, TtsData, TtsType } from '@/services/tts/tts-data';
import { AudioError, languageToVoicesMap } from '@/state/presenters';
import { IVoice, Providers, VoiceAccess, VoiceConfig } from '@d-id/types';
import { useRecoilValue } from 'recoil';

const MAX_CACHEABLE_TEXT_LENGTH = 250;
const MULTILINGUAL_TEXT_LIMIT = 800;

export const getTtsProperties = (voice: IVoice | null, text: string): { type: TtsType; cacheable: boolean } => {
    const isStreamProvider = voice?.provider === Providers.Elevenlabs;
    const isInternalVoice = voice?.access !== VoiceAccess.ExternalPrivate;
    const isTextCacheable =
        text.length < MAX_CACHEABLE_TEXT_LENGTH || (voice?.language === 'multilingual' && text.length < MULTILINGUAL_TEXT_LIMIT);
    const isAzureProvider = voice?.provider && [Providers.Microsoft, Providers.AzureOpenAi].includes(voice?.provider);

    if (isStreamProvider && isInternalVoice && isTextCacheable) {
        return { type: 'stream', cacheable: true };
    } else if (isAzureProvider) {
        return { type: 'synthesizer', cacheable: true };
    } else {
        return { type: 'synthesizer', cacheable: false };
    }
};

export const useGetTtsData = () => {
    const { data } = useTtsData();
    const languageToVoices = useRecoilValue(languageToVoicesMap);
    const { accessToken } = useAuth();
    const { wrapWithTiming, withTiming } = useTiming();

    const getTtsData = async (
        setInlineTtsError: (ttsError: AudioError) => void,
        voice: IVoice | null,
        text: string,
        voiceConfig: VoiceConfig
    ): Promise<TtsData> => {
        const { type } = getTtsProperties(voice, text);

        if (type === 'stream') {
            return withTiming('getStreamTtsData', () =>
                getStreamTtsData({
                    setInlineTtsError,
                    token: accessToken,
                    streamLambdaUrl: data?.streamLambdaUrl,
                    text,
                    voice,
                    voiceConfig,
                })
            );
        }

        return withTiming('getAzureTtsData', () =>
            getAzureTtsData({ setInlineTtsError, credentials: data?.credentials, text, voice, voiceConfig, languageToVoices })
        );
    };

    return { getTtsData: wrapWithTiming('getTtsData', getTtsData) as typeof getTtsData };
};
